import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RoleService } from "@/gen/omnios/v1/rbac_connect.js";
import { TokenService } from "@/gen/omnios/v1/token_connect.js";
import type { ApiToken, ServiceAccount } from "@/gen/omnios/v1/token_pb.js";
import { transport } from "@/lib/grpc";
import { SCOPE_GROUPS } from "@/lib/scopes";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	Check,
	ChevronLeft,
	Copy,
	Loader2,
	Plus,
	Search,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/service-accounts")({
	component: ServiceAccountsPage,
});

const client = createClient(TokenService, transport);
const roleClient = createClient(RoleService, transport);

interface RoleOption {
	value: string;
	label: string;
}

function formatDate(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function ServiceAccountsPage() {
	const { t } = useTranslation("settings");

	function relativeTime(ts: { seconds: bigint } | undefined): string {
		if (!ts) return t("tokens_relative_never");
		const diff = Date.now() - Number(ts.seconds) * 1000;
		if (diff < 60_000) return t("tokens_relative_just_now");
		if (diff < 3_600_000)
			return t("tokens_relative_min_ago", { count: Math.floor(diff / 60_000) });
		if (diff < 86_400_000)
			return t("tokens_relative_hours_ago", {
				count: Math.floor(diff / 3_600_000),
			});
		return t("tokens_relative_days_ago", {
			count: Math.floor(diff / 86_400_000),
		});
	}

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("sa_breadcrumb") },
	]);

	const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
	const [accounts, setAccounts] = useState<ServiceAccount[]>([]);
	const [loading, setLoading] = useState(true);
	const [showCreate, setShowCreate] = useState(false);
	const [search, setSearch] = useState("");
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const [selectedSA, setSelectedSA] = useState<ServiceAccount | null>(null);
	const [saTokens, setSaTokens] = useState<ApiToken[]>([]);
	const [showCreateToken, setShowCreateToken] = useState(false);
	const [createdPlaintext, setCreatedPlaintext] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [revokeTokenId, setRevokeTokenId] = useState<string | null>(null);

	const [saName, setSaName] = useState("");
	const [saDesc, setSaDesc] = useState("");
	const [saRole, setSaRole] = useState("viewer");

	const [tokenName, setTokenName] = useState("");
	const [tokenScopes, setTokenScopes] = useState<Set<string>>(new Set());
	const [tokenExpiry, setTokenExpiry] = useState("90");

	const loadAccounts = useCallback(async () => {
		setLoading(true);
		try {
			const res = await client.listServiceAccounts({});
			setAccounts(res.serviceAccounts);
		} finally {
			setLoading(false);
		}
	}, []);

	const loadSATokens = useCallback(async (saId: string) => {
		const res = await client.listServiceAccountTokens({
			serviceAccountId: saId,
		});
		setSaTokens(res.tokens);
	}, []);

	useEffect(() => {
		loadAccounts();
		roleClient.listRoles({}).then((resp) => {
			setRoleOptions(
				resp.roles
					.filter((r) => r.name !== "owner")
					.map((r) => ({
						value: r.name,
						label: r.displayName || r.name,
					})),
			);
		});
	}, [loadAccounts]);

	const handleCreateSA = async () => {
		const res = await client.createServiceAccount({
			name: saName.trim(),
			description: saDesc.trim(),
			role: saRole,
		});
		setShowCreate(false);
		setSaName("");
		setSaDesc("");
		setSaRole("viewer");
		loadAccounts();
		if (res.serviceAccount) {
			setSelectedSA(res.serviceAccount);
			loadSATokens(res.serviceAccount.id);
		}
	};

	const handleDeleteSA = async () => {
		if (!deleteId) return;
		await client.deleteServiceAccount({ id: deleteId });
		setDeleteId(null);
		if (selectedSA?.id === deleteId) {
			setSelectedSA(null);
			setSaTokens([]);
		}
		loadAccounts();
	};

	const handleSelectSA = (sa: ServiceAccount) => {
		setSelectedSA(sa);
		loadSATokens(sa.id);
	};

	const toggleScope = (scope: string) => {
		setTokenScopes((prev) => {
			const next = new Set(prev);
			if (next.has(scope)) next.delete(scope);
			else next.add(scope);
			return next;
		});
	};

	const handleCreateToken = async () => {
		if (!selectedSA) return;
		const res = await client.createServiceAccountToken({
			serviceAccountId: selectedSA.id,
			name: tokenName.trim(),
			scopes: [...tokenScopes],
			expiresInDays: Number.parseInt(tokenExpiry),
		});
		setCreatedPlaintext(res.plaintext);
		setShowCreateToken(false);
		setTokenName("");
		setTokenScopes(new Set());
		setTokenExpiry("90");
		loadSATokens(selectedSA.id);
	};

	const handleRevokeToken = async () => {
		if (!revokeTokenId || !selectedSA) return;
		await client.revokeToken({ tokenId: revokeTokenId });
		setRevokeTokenId(null);
		loadSATokens(selectedSA.id);
	};

	const handleCopy = async () => {
		if (!createdPlaintext) return;
		await navigator.clipboard.writeText(createdPlaintext);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// ── Detail view ──────────────────────────────────
	if (selectedSA) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-3">
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={() => {
							setSelectedSA(null);
							setSaTokens([]);
						}}
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<div>
						<h2 className="text-lg font-semibold">{selectedSA.name}</h2>
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							{selectedSA.description && <span>{selectedSA.description}</span>}
							<Badge variant="secondary" className="text-[10px]">
								{roleOptions.find((r) => r.value === selectedSA.role)?.label ??
									selectedSA.role}
							</Badge>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between">
					<p className="text-sm font-medium">{t("sa_tokens_label")}</p>
					<Button size="sm" onClick={() => setShowCreateToken(true)}>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						{t("sa_tokens_create")}
					</Button>
				</div>

				{saTokens.length === 0 ? (
					<Card className="px-4 py-8 text-center">
						<p className="text-sm text-muted-foreground">
							{t("sa_tokens_empty")}
						</p>
					</Card>
				) : (
					saTokens.map((tk) => (
						<Card key={tk.id} className="px-4 py-4">
							<div className="flex items-start justify-between gap-3">
								<div className="flex-1 min-w-0 space-y-1">
									<div className="flex items-center gap-2">
										<h3 className="text-sm font-medium truncate">{tk.name}</h3>
										<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium font-mono text-muted-foreground">
											{tk.tokenPrefix}...
										</span>
										{tk.revokedAt && (
											<Badge variant="destructive" className="text-[10px]">
												{t("sa_token_revoked")}
											</Badge>
										)}
									</div>
									<div className="flex gap-3 text-xs text-muted-foreground">
										<span>
											{t("sa_token_created", {
												date: formatDate(tk.createdAt),
											})}
										</span>
										<span>
											{t("sa_token_expires", {
												date: formatDate(tk.expiresAt),
											})}
										</span>
										<span>
											{t("sa_token_used", {
												time: relativeTime(tk.lastUsedAt),
											})}
										</span>
									</div>
									{tk.scopes.length > 0 && (
										<div className="flex flex-wrap gap-1 pt-0.5">
											{tk.scopes.map((s) => (
												<Badge
													key={s}
													variant="secondary"
													className="text-[10px]"
												>
													{s}
												</Badge>
											))}
										</div>
									)}
								</div>
								{!tk.revokedAt && (
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 text-destructive hover:text-destructive"
										title={t("tokens_revoke_title_button")}
										onClick={() => setRevokeTokenId(tk.id)}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								)}
							</div>
						</Card>
					))
				)}

				{/* Create Token Dialog */}
				<Dialog open={showCreateToken} onOpenChange={setShowCreateToken}>
					<DialogContent className="sm:max-w-lg">
						<DialogHeader>
							<DialogTitle>{t("sa_token_create_title")}</DialogTitle>
							<DialogDescription>
								{t("sa_token_create_desc", { name: selectedSA.name })}
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-2">
							<div className="space-y-1.5">
								<Label>{t("tokens_label_name")}</Label>
								<Input
									placeholder={t("sa_token_name_placeholder")}
									value={tokenName}
									onChange={(e) => setTokenName(e.target.value)}
								/>
							</div>
							<div className="space-y-1.5">
								<Label>{t("tokens_label_expiry")}</Label>
								<Select value={tokenExpiry} onValueChange={setTokenExpiry}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="7">{t("tokens_expiry_7")}</SelectItem>
										<SelectItem value="30">{t("tokens_expiry_30")}</SelectItem>
										<SelectItem value="90">{t("tokens_expiry_90")}</SelectItem>
										<SelectItem value="180">
											{t("tokens_expiry_180")}
										</SelectItem>
										<SelectItem value="365">
											{t("tokens_expiry_365")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1.5">
								<Label>{t("tokens_label_permissions")}</Label>
								<div className="max-h-48 space-y-3 overflow-y-auto rounded-md border p-3">
									{SCOPE_GROUPS.map((group) => (
										<div key={group.resource}>
											<p className="text-xs font-medium text-muted-foreground mb-1">
												{group.label}
											</p>
											<div className="flex flex-wrap gap-x-4 gap-y-1">
												{group.actions.map((a) => {
													const scope = `${group.resource}:${a.action}`;
													return (
														<label
															key={scope}
															className="flex items-center gap-1.5 text-sm cursor-pointer"
														>
															<Checkbox
																checked={tokenScopes.has(scope)}
																onCheckedChange={() => toggleScope(scope)}
															/>
															{a.label}
														</label>
													);
												})}
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowCreateToken(false)}
							>
								{t("common:cancel")}
							</Button>
							<Button
								size="sm"
								onClick={handleCreateToken}
								disabled={!tokenName.trim()}
							>
								{t("common:create")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Plaintext show-once */}
				<Dialog
					open={!!createdPlaintext}
					onOpenChange={(o) => {
						if (!o) setCreatedPlaintext(null);
					}}
				>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>{t("tokens_created_title")}</DialogTitle>
							<DialogDescription>{t("tokens_created_desc")}</DialogDescription>
						</DialogHeader>
						<div className="space-y-1.5">
							<div className="flex gap-2">
								<Input
									readOnly
									value={createdPlaintext ?? ""}
									className="font-mono text-xs"
								/>
								<Button variant="outline" size="icon" onClick={handleCopy}>
									{copied ? (
										<Check className="h-3.5 w-3.5 text-green-600" />
									) : (
										<Copy className="h-3.5 w-3.5" />
									)}
								</Button>
							</div>
							{copied && (
								<p className="text-xs text-muted-foreground">
									{t("tokens_copied")}
								</p>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setCreatedPlaintext(null)}
							>
								{t("tokens_done")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Revoke confirm */}
				<AlertDialog
					open={!!revokeTokenId}
					onOpenChange={(o) => {
						if (!o) setRevokeTokenId(null);
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t("tokens_revoke_title")}</AlertDialogTitle>
							<AlertDialogDescription>
								{t("tokens_revoke_desc")}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
							<AlertDialogAction
								onClick={handleRevokeToken}
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							>
								{t("tokens_revoke_action")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		);
	}

	const filtered = accounts.filter(
		(a) =>
			!search ||
			a.name.toLowerCase().includes(search.toLowerCase()) ||
			a.description.toLowerCase().includes(search.toLowerCase()),
	);

	// ── List view ────────────────────────────────────
	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("sa_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button size="sm" onClick={() => setShowCreate(true)}>
					<Plus className="h-3.5 w-3.5 mr-1.5" />
					{t("sa_create_account")}
				</Button>
			</div>

			{loading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			)}

			{!loading && filtered.length === 0 && (
				<Card className="px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">{t("sa_empty")}</p>
				</Card>
			)}

			{filtered.map((sa) => (
				<Card
					key={sa.id}
					className="px-4 py-4 cursor-pointer transition-colors hover:bg-accent/50"
					onClick={() => handleSelectSA(sa)}
				>
					<div className="flex items-start justify-between gap-3">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<Bot className="h-4 w-4 shrink-0 text-muted-foreground" />
								<h3 className="text-sm font-medium truncate">{sa.name}</h3>
								<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground uppercase">
									{roleOptions.find((r) => r.value === sa.role)?.label ??
										sa.role}
								</span>
								{!sa.isActive && (
									<span className="shrink-0 h-2 w-2 rounded-full bg-slate-300" />
								)}
							</div>
							{sa.description && (
								<p className="text-xs text-muted-foreground mt-0.5 truncate">
									{sa.description}
								</p>
							)}
							<p className="text-xs text-muted-foreground mt-0.5">
								{t("sa_created", { date: formatDate(sa.createdAt) })}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-destructive hover:text-destructive"
							title={t("sa_delete_title_btn")}
							onClick={(e) => {
								e.stopPropagation();
								setDeleteId(sa.id);
							}}
						>
							<Trash2 className="h-3.5 w-3.5" />
						</Button>
					</div>
				</Card>
			))}

			{/* Create SA dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("sa_create_title")}</DialogTitle>
						<DialogDescription>{t("sa_create_desc")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-1.5">
							<Label>{t("tokens_label_name")}</Label>
							<Input
								placeholder={t("sa_create_name_placeholder")}
								value={saName}
								onChange={(e) => setSaName(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>{t("sa_create_desc_label")}</Label>
							<Textarea
								placeholder={t("sa_create_desc_placeholder")}
								value={saDesc}
								onChange={(e) => setSaDesc(e.target.value)}
								rows={2}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>{t("sa_create_role_label")}</Label>
							<Select value={saRole} onValueChange={setSaRole}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{roleOptions.map((r) => (
										<SelectItem key={r.value} value={r.value}>
											{r.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowCreate(false)}
						>
							{t("common:cancel")}
						</Button>
						<Button
							size="sm"
							onClick={handleCreateSA}
							disabled={!saName.trim()}
						>
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete SA confirm */}
			<AlertDialog
				open={!!deleteId}
				onOpenChange={(o) => {
					if (!o) setDeleteId(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("sa_delete_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("sa_delete_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteSA}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
